import axios, { AxiosError } from "axios";
import { proxyUrl } from "../config";

export type TWRUserInformation = {
  iD: string;
  name: string;
  gender: string;
  birth_date: string;
  email: string;
  cell_phone: string;
  address: string;
  city: string;
  country: string;
  postal_code: string;
  birth_place: string;
  marital_status: string;
  job: string;
}

enum WRAccountSubtypeEnum {
  checking = "checking",
  lineOfCredit = "line-of-credit"
}

// enum WROwnerRolesEnum {
//   owner = "owner",
//   legalRepresentative = "legal representative",
//   administrator = "administrator",
//   attorneyInFact = "attorney-in-fact",
//   authorised = "authorised"
// }

export type TWROwner = {
  id: string;
  name: string;
  role: string
}

export type TWRAccountBalance = {
  available: number;
  current: number;
}

type TTransactionWRAccount = {
  uuid: string;
  value_date: string;
  operation_date: string;
  amount: number;
  balance: number;
  description: string;
  additional_info: []
}

export type TWRAccount = {
  uuid: string;
  subtype?: WRAccountSubtypeEnum | null;
  line_of_credit_details?: any | null;
  code: string;
  name: string;
  currency: string;
  owners: TWROwner[];
  balances: TWRAccountBalance;
  transactions: TTransactionWRAccount[];
}

// enum WRPortfolioSubtypesEnum {
//   funds = "funds",
//   stocks = "stocks",
//   pensionPlans = "pension-plans",
//   alternativeInvestments = "alternative-investments"
// }

export type TWRComposition = {
  isin?: string | null;
  isiN_description?: string | null;
  dgs?: string | null;
  dgS_description?: string | null;
  currency?: string | null;
  weighting?: number | null;
  shares?: number | null;
  price?: number | null;
  total_amount?: number | null;
}

// enum WROperationTypeEnum {
//   purchase = "purchase",
//   sale = "sale",
//   subscriptionByTransfer = "subscription by transfer",
//   redemptionByTransfer = "redemption by transfer",
//   scripDividend = "scrip dividend",
//   capitalIncrease = "capital increase",
//   equityRights = "equity rights",
//   equityAdjustment = "equity adjustment"
// }

export type TWPTransaction = {
  uuid: string;
  operation_pending?: string | null;
  operation_date?: string | null;
  value_date: string;
  value_time?: string | null;
  operation_type: string;
  ISIN?: string | null;
  DGS?: string | null;
  shares: number;
  price: number;
  total_amount: number;
  reference_currency: string;
  currency: string;
  exchange_rate: number;
}

export type TWRPortfolio = {
  subtype: string;
  uuid: string;
  code: string;
  name: string;
  currency?: string | null;
  total_value?: number | null;
  weighted_return?: number | null;
  annualized_volatility?: number | null;
  yield?: number | null;
  contributions?: number | null;
  withholdings?: number | null;
  contracting_date?: string | null;
  linked_account?: string | null;
  owners: TWROwner[];
  composition: TWRComposition[];
  transactions: TWPTransaction[];
}

type THolders = {
  id: string;
  name: string;
}

type TAmortization = {
  uuid: string;
  date: string;
  amortized: number;
  interest: number;
  total: number;
  pending_capital: number;
  operation_type: string;
}

type TLoans = {
  uuid: string;
  contract_number: string;
  loan_type: string;
  constitution_date: string;
  currency: string;
  granted_capital: number;
  pending_amount: number;
  next_installment: number;
  linked_account: string;
  holders: THolders[];
  interest_rate: number;
  expiration_date: string;
  pending_receipts: number;
  amortization_made: TAmortization[];
  amortization_pending: TAmortization[];
}

type TInsuredValue = {
  name: string;
  value: number;
}
type TInsuredConcepts = {
  description: string;
  value: number;
}

type TInsurances = {
  uuid: string;
  code: string;
  coverage_category: string;
  currency: string;
  insured_value: TInsuredValue;
  insured_concepts: TInsuredConcepts[];
}

type TOwnerCard = {
  ID: string;
  name: string;
  role: string;
}

type TBalancesCard = {
  limit: number;
  available: number;
  disposed: number;
}

type TTransactionCard = {
  uuid: string;
  settled: boolean
  value_date: string;
  operation_date: string;
  amount: number;
  description: string;
  categorization: {
    type: number;
  }
}

type TCard = {
  uuid: string;
  linked_account: string;
  next_payment_date: string | null;
  owner: TOwnerCard[];
  code: string;
  status: string;
  subtype: string;
  name: string;
  currency: string;
  anual_interest: string | null;
  tae: string | null;
  balances: TBalancesCard;
  transactions: TTransactionCard[];
}

type TWRPayload = {
  user_information: TWRUserInformation;
  accounts: TWRAccount[];
  portfolios: TWRPortfolio[];
  cards: TCard[];
  loans: TLoans[];
  insurances: TInsurances[];
}

export type TWealthReaderItem = {
  id: number;
  operationId: string;
  payload: TWRPayload;
};

// enum WRWarningProductTypeEnum {
//   user_information = "user_information",
//   accounts = "accounts",
//   portfolios = "portfolios",
//   bonds = "bonds",
//   cards = "cards",
//   receipts = "receipts",
//   loans = "loans",
//   factoring = "factoring",
//   confirming = "confirming",
//   properties = "properties",
//   invoices = "invoices",
//   files = "files",
//   unknown = "unknown"
// }

type TWRWarnings = {
  code: number;
  product_type: string;
  message: string;
}

export type TWRStatistics = {
  SESSION: string;
  code: string;
  execution_time: number;
  operation_id: string;
  warnings: TWRWarnings[];
  token: string;
}

export type TWealthReaderCallback = {
  success: boolean;
  payload: TWRPayload;
  statistics: TWRStatistics;
};

export type TWealthReaderPayload = {
  operationId: string;
  token: string;
}

export type TCreateSessionResponse = {
  operationId: string;
  linkToken?: string;
  redirectUrl?: string;
};

const headers = { "Content-Type": "application/json" };

class WealthReaderService {
  public wealthreaderItem = async (operationId: string): Promise<TWRPortfolio[]> => {
    const url: string = `${proxyUrl}/wealthreaderItem/${operationId}`;
    try {
      const req = await axios.get<TWRPortfolio[]>(url, { headers });
      if (!req.data) {
        throw new Error(`Respuesta vacía del servidor - Error HTTP: ${req.status}`);
      };

      return req.data;
    } catch (error) {
      this.handleError(error);
    }
  };

  public wealthreaderCallback = async (payload: TWealthReaderPayload): Promise<TWealthReaderCallback> => {
    const url: string = `${proxyUrl}/wealthreader/callback`;

    try {
      const req = await axios.post<TWealthReaderCallback>(url, payload, { headers });
      if (!req.data) {
        throw new Error(`Respuesta vacía del servidor - Error HTTP: ${req.status}`);
      };

      return req.data;
    } catch (error) {
      this.handleError(error);
    }
  };

  public createWealthReaderSession = async (): Promise<TCreateSessionResponse> => {
    const url = `${proxyUrl}/wealthreader/session`;
    try {
      const res = await axios.post<TCreateSessionResponse>(url, {}, { headers });
      if (!res.data) {
        throw new Error(`Respuesta vacía - HTTP ${res.status}`);
      }
      return res.data;
    } catch (error) {
      this.handleError(error);
    }
  };


  private handleError(error: unknown): never {
    const err = error as AxiosError;

    console.error("Error API:", {
      message: err.message,
      status: err.response?.status,
      data: err.response?.data,
    });

    throw err;
  }
};

const wealthReaderService = new WealthReaderService()
export default wealthReaderService;
